import {
  ApolloCache,
  ApolloError,
  QueryResult,
  useLazyQuery,
  useQuery,
} from '@apollo/client';
import { READ_PARKING_SESSIONS } from '@queries/readParkingSessions';
import AccountContext from 'context/AccountContext';
import { isAfter } from 'date-fns';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import { useContext, useEffect, useMemo, useState } from 'react';
import { latestVehicleUsed } from 'states/persistInStorage';
import { useParkingStore } from 'stores/parkingStore';
import {
  Maybe,
  ParkingPaymentState,
  ParkingSession,
  ParkingSessions,
  ParkingState,
  Query,
  QueryParkZoneArgs,
  QueryReadParkingSessionsArgs,
  SessionType,
} from 'types/generatedSchemaTypes';
import { emptyQuery } from 'utils/commonUtils';
import { updateStateAndStorage } from 'utils/MMKVStorageUtils';
import useParkingZoneWithEVCheck from './useParkingZoneWithEVCheck';

const getVehicleLabel = (name: string, licensePlate: string) => {
  if (name) {
    return `${name} (${licensePlate})`;
  }
  return licensePlate;
};

const input = {
  parkingState: ['Started'] as ParkingState[],
  sessionType: ['ANPR', 'START_STOP'] as SessionType[],
};

const sessionPaymentPendingInput = {
  parkingState: ['NotStarted'] as ParkingState[],
  sessionType: ['START_STOP'] as SessionType[],
  paymentState: ['NotInitiated'] as ParkingPaymentState[],
};

const useOngoingParkingSession = (
  bypassCache = false,
  skipInitialLoading = false
): {
  parkingZoneResult?: QueryResult<Query, QueryParkZoneArgs>;
  ongoingParkingSession?: Maybe<ParkingSession>;
  allParkingSessions?: Maybe<ParkingSession>[];
  pendingSessions?: Maybe<ParkingSession>[];
  ongoingParkingSessionLoading?: boolean;
  ongoingParkingSessionError?: ApolloError;
  ongoingParkingSessionStartPolling: (pollInterval: number) => void;
  ongoingParkingSessionStopPolling: () => void;
  ongoingParkingSessionRefetch: () => void;
  getPaymentPendingSession: () => void;
  addOngoingSessionToCache: (
    cache: ApolloCache<any>,
    parkingSession: Maybe<ParkingSession> | undefined
  ) => void;
} => {
  const { isLoggedIn } = useIsLoggedIn();
  const selectedSessionId = useParkingStore(
    (state) => state.selectedSessionIdFromMultiple
  );
  const {
    customer: { vehicles },
  } = useContext(AccountContext) || [];
  const [initialLoadingCompleted, setInitialLoadingCompleted] = useState(false);

  const [getPaymentPendingSession, { data: pendingSessions } = emptyQuery] =
    useLazyQuery<Query, QueryReadParkingSessionsArgs>(READ_PARKING_SESSIONS, {
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      refetchWritePolicy: 'merge',
      variables: {
        input: sessionPaymentPendingInput,
      },
    });

  const { data, loading, error, startPolling, stopPolling, refetch } = useQuery<
    Query,
    QueryReadParkingSessionsArgs
  >(READ_PARKING_SESSIONS, {
    fetchPolicy: bypassCache ? 'network-only' : 'cache-and-network',
    skip: !isLoggedIn,
    refetchWritePolicy: 'merge',
    variables: {
      input,
    },
    onCompleted: ({ readParkingSessions }) => {
      const licensePlate =
        readParkingSessions?.content &&
        readParkingSessions.content[0]?.accessDevice?.value;
      if (licensePlate) {
        updateStateAndStorage(
          latestVehicleUsed,
          'latestVehicleUsed',
          licensePlate
        );
      }
      setInitialLoadingCompleted(true);
    },
  });

  const ongoingShortTermParkingSessions =
    data?.readParkingSessions?.content || [];

  const sessionToShow = () => {
    if (
      ongoingShortTermParkingSessions.length === 1 ||
      (ongoingShortTermParkingSessions.length > 1 && selectedSessionId === null)
    ) {
      const session = ongoingShortTermParkingSessions[0];
      const endDate = session?.endDate ?? session?.predefinedEndDate;

      // Check if session has expired
      if (endDate && isAfter(new Date(), new Date(endDate))) {
        return null;
      }

      return session;
    }

    const foundSession =
      ongoingShortTermParkingSessions.find(
        (parkingSession) => parkingSession?.sessionId === selectedSessionId
      ) ?? ongoingShortTermParkingSessions?.find(Boolean);

    // Check if found session has expired
    if (foundSession) {
      const endDate = foundSession.endDate ?? foundSession.predefinedEndDate;
      if (endDate && isAfter(new Date(), new Date(endDate))) {
        return null;
      }
    }
    return foundSession;
  };
  // Atm we have design and case for only one ongoing session so pick first ongoing in array
  const session = sessionToShow();

  const {
    parkingZoneUid,
    sessionId = '',
    accessDevice,
    type: parkingSessionType,
  } = session || {};
  const licensePlate = accessDevice?.value || '';

  const parkingZoneResult = useParkingZoneWithEVCheck(parkingZoneUid ?? '');

  const endTime = useMemo(
    () =>
      session?.predefinedEndDate
        ? new Date(session?.predefinedEndDate as string)
        : null,
    [session?.predefinedEndDate]
  );
  const { name } =
    vehicles?.find((v) => v.licensePlate?.plateText === licensePlate) || {};
  const vehicleLabel = getVehicleLabel(name || '', licensePlate);

  useEffect(() => {
    useParkingStore.getState().setOngoingParkingSession({
      sessionId,
      vehicleLabel,
      endTime,
      parkingSessionType: parkingSessionType,
      parkZoneUid: parkingZoneUid as string,
    });
  }, [sessionId, vehicleLabel, endTime, parkingSessionType, parkingZoneUid]);

  const addOngoingSessionToCache = (
    cache: ApolloCache<any>,
    parkingSession: Maybe<ParkingSession> | undefined
  ) => {
    if (!parkingSession) {
      return;
    }
    cache?.updateQuery(
      {
        query: READ_PARKING_SESSIONS,
        variables: { input },
        overwrite: true,
      },
      (data: Query | null) => {
        return {
          readParkingSessions: {
            ...data?.readParkingSessions,
            content: [
              ...(data?.readParkingSessions?.content ?? []),
              parkingSession,
            ] as ParkingSession[],
          } as ParkingSessions,
        };
      }
    );
  };

  return {
    parkingZoneResult,
    ongoingParkingSession: session,
    allParkingSessions: ongoingShortTermParkingSessions,
    ongoingParkingSessionLoading:
      loading && (skipInitialLoading ? initialLoadingCompleted : true),
    ongoingParkingSessionError: error,
    pendingSessions: pendingSessions?.readParkingSessions?.content || [],
    ongoingParkingSessionStartPolling: startPolling,
    getPaymentPendingSession,
    ongoingParkingSessionStopPolling: stopPolling,
    ongoingParkingSessionRefetch: refetch,
    addOngoingSessionToCache,
  };
};

export default useOngoingParkingSession;
